'use client';
import { useRef } from 'react';
import { motion, useInView } from 'motion/react';
import {
  TrendingUp,
  AlertTriangle,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';

const mockSystems = [
  { name: 'Main SCADA', tier: 1, exposure: 847500, trend: 'up' },
  { name: 'PLC Network', tier: 1, exposure: 612000, trend: 'down' },
  { name: 'Safety System', tier: 1, exposure: 523000, trend: 'up' },
  { name: 'HMI Displays', tier: 2, exposure: 245000, trend: 'stable' },
  { name: 'Historian', tier: 2, exposure: 180000, trend: 'down' },
];

const tierData = [
  { tier: 'Tier 1', count: 5, exposure: 1982500, color: 'amber' },
  { tier: 'Tier 2', count: 8, exposure: 645000, color: 'slate' },
  { tier: 'Tier 3', count: 12, exposure: 220000, color: 'teal' },
];

export default function DashboardPreview() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="dashboard-preview" ref={ref} className="relative py-24 lg:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-mesh" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[hsl(var(--slate-dark))]/5 border border-[hsl(var(--slate-dark))]/10 text-sm font-medium text-[hsl(var(--slate-dark))] mb-6">
            <BarChart3 className="w-4 h-4" />
            Insights at a Glance
          </span>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-[hsl(var(--slate-deep))] mb-6 text-balance">
            Visualize Your{' '}
            <span className="text-gradient-amber">Financial Exposure</span>
          </h2>

          <p className="text-lg text-[hsl(var(--slate-medium))] leading-relaxed">
            Interactive dashboards that turn complex assessments into actionable
            insights your board can understand.
          </p>
        </motion.div>

        {/* Dashboard Preview */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative"
        >
          {/* Glow effect */}
          <div className="absolute -inset-4 bg-gradient-to-r from-[hsl(var(--amber))]/10 via-[hsl(var(--teal))]/5 to-[hsl(var(--amber))]/10 rounded-3xl blur-2xl opacity-60" />

          {/* Main dashboard container */}
          <div className="relative bg-white rounded-2xl shadow-2xl border border-[hsl(var(--border))] overflow-hidden">
            {/* Window chrome */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-amber-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="px-4 py-1.5 rounded-lg bg-white border border-[hsl(var(--border))] text-sm text-[hsl(var(--slate-medium))]">
                  app.impactot.com/dashboard
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-[hsl(var(--slate-medium))]">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                Live
              </div>
            </div>

            {/* Dashboard content */}
            <div className="p-6 lg:p-8 space-y-6">
              {/* Top row - Key metrics */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Total Exposure */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.4 }}
                  className="p-5 rounded-xl bg-gradient-to-br from-[hsl(var(--amber))]/10 to-[hsl(var(--amber))]/5 border border-[hsl(var(--amber))]/20"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-xs font-medium text-[hsl(var(--amber))] uppercase tracking-wider mb-1">
                        24h Exposure
                      </div>
                      <div className="text-2xl lg:text-3xl font-mono font-bold text-[hsl(var(--slate-deep))]">
                        £2.8M
                      </div>
                    </div>
                    <div className="p-2 rounded-lg bg-[hsl(var(--amber))]/20">
                      <TrendingUp className="w-5 h-5 text-[hsl(var(--amber))]" />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 mt-2 text-xs text-[hsl(var(--teal))]">
                    <ArrowDownRight className="w-3 h-3" />
                    <span>12% from last assessment</span>
                  </div>
                </motion.div>

                {/* Critical Systems */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.5 }}
                  className="p-5 rounded-xl bg-white border border-[hsl(var(--border))]"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-xs font-medium text-[hsl(var(--slate-medium))] uppercase tracking-wider mb-1">
                        Tier 1 Critical
                      </div>
                      <div className="text-2xl lg:text-3xl font-mono font-bold text-[hsl(var(--slate-deep))]">
                        5
                      </div>
                    </div>
                    <div className="p-2 rounded-lg bg-[hsl(var(--destructive))]/10">
                      <AlertTriangle className="w-5 h-5 text-[hsl(var(--destructive))]" />
                    </div>
                  </div>
                  <div className="text-xs text-[hsl(var(--slate-medium))] mt-2">
                    Requires immediate attention
                  </div>
                </motion.div>

                {/* Recovery Gaps */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.6 }}
                  className="p-5 rounded-xl bg-white border border-[hsl(var(--border))]"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-xs font-medium text-[hsl(var(--slate-medium))] uppercase tracking-wider mb-1">
                        Recovery Gaps
                      </div>
                      <div className="text-2xl lg:text-3xl font-mono font-bold text-[hsl(var(--amber))]">
                        3
                      </div>
                    </div>
                    <div className="p-2 rounded-lg bg-[hsl(var(--amber))]/10">
                      <BarChart3 className="w-5 h-5 text-[hsl(var(--amber))]" />
                    </div>
                  </div>
                  <div className="text-xs text-[hsl(var(--slate-medium))] mt-2">
                    RTO exceeds MTD
                  </div>
                </motion.div>

                {/* Total Systems */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.7 }}
                  className="p-5 rounded-xl bg-white border border-[hsl(var(--border))]"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-xs font-medium text-[hsl(var(--slate-medium))] uppercase tracking-wider mb-1">
                        Systems Assessed
                      </div>
                      <div className="text-2xl lg:text-3xl font-mono font-bold text-[hsl(var(--slate-deep))]">
                        25
                      </div>
                    </div>
                    <div className="p-2 rounded-lg bg-[hsl(var(--teal))]/10">
                      <PieChart className="w-5 h-5 text-[hsl(var(--teal))]" />
                    </div>
                  </div>
                  <div className="text-xs text-[hsl(var(--slate-medium))] mt-2">
                    Across 4 functions
                  </div>
                </motion.div>
              </div>

              {/* Bottom row - Charts and table */}
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Risk breakdown by tier */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.8 }}
                  className="p-5 rounded-xl bg-white border border-[hsl(var(--border))]"
                >
                  <h4 className="text-sm font-medium text-[hsl(var(--slate-dark))] mb-4">
                    Exposure by Risk Tier
                  </h4>

                  {/* Visual bar chart */}
                  <div className="space-y-3">
                    {tierData.map((tier, i) => {
                      const maxExposure = Math.max(
                        ...tierData.map((t) => t.exposure)
                      );
                      const percentage = (tier.exposure / maxExposure) * 100;
                      const colorClass =
                        tier.color === 'amber'
                          ? 'bg-[hsl(var(--amber))]'
                          : tier.color === 'slate'
                            ? 'bg-[hsl(var(--slate-dark))]'
                            : 'bg-[hsl(var(--teal))]';

                      return (
                        <div key={tier.tier} className="space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-[hsl(var(--slate-dark))]">
                              {tier.tier} ({tier.count})
                            </span>
                            <span className="font-mono text-[hsl(var(--slate-medium))]">
                              £{(tier.exposure / 1000).toFixed(0)}K
                            </span>
                          </div>
                          <div className="h-2 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={
                                isInView ? { width: `${percentage}%` } : {}
                              }
                              transition={{ duration: 1, delay: 0.9 + i * 0.1 }}
                              className={`h-full rounded-full ${colorClass}`}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>

                {/* Top risk systems table */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.9 }}
                  className="lg:col-span-2 p-5 rounded-xl bg-white border border-[hsl(var(--border))]"
                >
                  <h4 className="text-sm font-medium text-[hsl(var(--slate-dark))] mb-4">
                    Top Risk Systems
                  </h4>

                  <div className="overflow-hidden rounded-lg border border-[hsl(var(--border))]">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-[hsl(var(--muted))]/50 text-xs text-[hsl(var(--slate-medium))]">
                          <th className="text-left px-4 py-2 font-medium">
                            System
                          </th>
                          <th className="text-center px-4 py-2 font-medium">
                            Tier
                          </th>
                          <th className="text-right px-4 py-2 font-medium">
                            24h Exposure
                          </th>
                          <th className="text-center px-4 py-2 font-medium">
                            Trend
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[hsl(var(--border))]">
                        {mockSystems.map((system, i) => (
                          <motion.tr
                            key={system.name}
                            initial={{ opacity: 0, x: -10 }}
                            animate={isInView ? { opacity: 1, x: 0 } : {}}
                            transition={{ delay: 1 + i * 0.1 }}
                            className="text-sm"
                          >
                            <td className="px-4 py-3 text-[hsl(var(--slate-dark))] font-medium">
                              {system.name}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span
                                className={`px-2 py-0.5 rounded text-xs font-medium ${system.tier === 1
                                    ? 'bg-[hsl(var(--amber))]/10 text-[hsl(var(--amber))]'
                                    : 'bg-[hsl(var(--slate-dark))]/10 text-[hsl(var(--slate-dark))]'
                                  }`}
                              >
                                Tier {system.tier}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-[hsl(var(--slate-dark))]">
                              £{system.exposure.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {system.trend === 'up' && (
                                <ArrowUpRight className="w-4 h-4 text-[hsl(var(--destructive))] inline" />
                              )}
                              {system.trend === 'down' && (
                                <ArrowDownRight className="w-4 h-4 text-[hsl(var(--teal))] inline" />
                              )}
                              {system.trend === 'stable' && (
                                <span className="text-[hsl(var(--slate-medium))]">
                                  —
                                </span>
                              )}
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>

          {/* Floating accents */}
          <div className="absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-br from-[hsl(var(--amber))] to-[hsl(var(--amber-light))] rounded-full opacity-20 blur-2xl" />
          <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-gradient-to-tr from-[hsl(var(--teal))] to-[hsl(var(--teal-light))] rounded-full opacity-15 blur-3xl" />
        </motion.div>
      </div>
    </section>
  );
}
